// @ts-nocheck
import React from 'react';
import InteractiveSlider from '../InteractiveSlider';
import { CarComparisonData } from '../../types/carComparison';
import { getTaxAmount, getExpectedValueAtEnd, getSellingCosts } from '../../utils/carCalculations';
import { renderInputSection } from '../../utils/renderInputSection';

interface BuyInputsProps {
  data: CarComparisonData;
  onInputChange: (field: keyof CarComparisonData, value: any) => void;
  showGuidance: (title: string, content: string) => void;
  formatCurrency: (value: number) => string;
}

const BuyInputs: React.FC<BuyInputsProps> = ({
  data,
  onInputChange,
  showGuidance,
  formatCurrency
}) => {

  return (
    renderInputSection('Buy Scenario', (
      <>
        {/* New/Used Toggle */}
        <div className="interactive-slider">
          <div className="label-with-guidance" style={{paddingBottom: "0.5rem"}}>
            <div style={{fontWeight: "bold"}}>Car Condition</div>
            <button
              onClick={() => showGuidance(
                'New vs Used Car',
                '<strong>New Car:</strong><br>' +
                '• Includes production emissions in CO₂ comparison<br>' +
                '• Starting mileage is 0<br>' +
                '• May qualify for rebates<br><br>' +
                '<strong>Used Car:</strong><br>' +
                '• Production emissions already accounted for<br>' +
                '• Enter current mileage<br>' +
                '• Lower purchase price but may have higher maintenance'
              )}
              className="guidance-btn"
              title="Get guidance on new vs used cars"
            >
              ℹ
            </button>
          </div>
          <div className="tax-toggle-buttons">
            <button
              type="button"
              className={`tax-toggle-btn ${data.isNewCar ? 'active' : ''}`}
              onClick={() => {
                onInputChange('isNewCar', true);
                onInputChange('openingMileage', 0);
              }}
            >
              New
            </button>
            <button
              type="button"
              className={`tax-toggle-btn ${!data.isNewCar ? 'active' : ''}`}
              onClick={() => {
                onInputChange('isNewCar', false);
                onInputChange('manufacturingEmissions', 0);
              }}
            >
              Used
            </button>
          </div>
        </div>

        {/* Opening Mileage for Used Cars */}
        {!data.isNewCar && (
          <InteractiveSlider
            config={{
              min: 0,
              max: 300000,
              step: 1000,
              defaultValue: 50000,
              format: 'number',
              label: 'Opening Mileage (km)'
            }}
            value={data.openingMileage || 0}
            onChange={(value) => onInputChange('openingMileage', value)}
            guidanceTitle="Opening Mileage"
            guidanceContent={'What is the current mileage on the odometer?<br><br>' +
              'This affects:<br>' +
              '• Maintenance cost estimates<br>' +
              '• Remaining useful life<br>' +
              '• Resale value projections<br><br>' +
              'Higher mileage vehicles typically have higher annual maintenance costs.'}
            onShowGuidance={showGuidance}
          />
        )}

        {/* Production Emissions Category for New Cars */}
        {data.isNewCar && data.includeCO2Comparison && (
          <div className="interactive-slider">
            <div className="label-with-guidance" style={{paddingBottom: "0.5rem"}}>
              <div style={{fontWeight: "bold"}}>Production Emissions</div>
              <button
                onClick={() => showGuidance(
                  'Vehicle Production Emissions',
                  'Manufacturing a vehicle creates significant CO₂ emissions.<br><br>' +
                  '<strong>Low (6-8 tonnes):</strong> Small gas cars (Corolla, Civic)<br>' +
                  '<strong>Medium (10-12 tonnes):</strong> Mid-size cars, small SUVs<br>' +
                  '<strong>High (15-20 tonnes):</strong> Large SUVs, trucks<br>' +
                  '<strong>Very High (25-35 tonnes):</strong> Large EVs (electric Hummer), luxury vehicles<br><br>' +
                  'Electric vehicles often have higher production emissions due to battery manufacturing, ' +
                  'but lower operating emissions over their lifetime.'
                )}
                className="guidance-btn"
                title="Get guidance on production emissions"
              >
                ℹ
              </button>
            </div>
            <select
              value={data.productionEmissionsCategory || 'medium'}
              onChange={(e) => {
                const category = e.target.value as 'low' | 'medium' | 'high' | 'very-high';
                onInputChange('productionEmissionsCategory', category);
                // Update manufacturingEmissions based on category
                const emissionsMap = {
                  'low': 7000,        // 7 tonnes
                  'medium': 11000,    // 11 tonnes
                  'high': 17500,      // 17.5 tonnes
                  'very-high': 30000  // 30 tonnes
                };
                onInputChange('manufacturingEmissions', emissionsMap[category]);
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '1rem',
                borderRadius: '4px',
                border: '1px solid #cbd5e0',
                backgroundColor: '#fff'
              }}
            >
              <option value="low">Low (6-8 tonnes CO₂) - Small gas car</option>
              <option value="medium">Medium (10-12 tonnes CO₂) - Mid-size car/SUV</option>
              <option value="high">High (15-20 tonnes CO₂) - Large SUV/truck</option>
              <option value="very-high">Very High (25-35 tonnes CO₂) - Large EV/luxury</option>
            </select>
          </div>
        )}

        <InteractiveSlider
          config={{
            min: 0,
            max: 1000000,
            step: 1000,
            defaultValue: 35000,
            format: 'number',
            label: 'Total Cost'
          }}
          value={data.totalCost}
          onChange={(value) => onInputChange('totalCost', value)}
          guidanceTitle="Total Cost"
          guidanceContent={'What\'s the total price of the car?<br><br>' +
            '<strong>Include:</strong><br>' +
            '• Base price of the vehicle<br>' +
            '• Any dealer add-ons or packages<br>' +
            '• Extended warranties (if included)<br>' +
            '• Delivery charges<br><br>' +
            '<strong>Don\'t include:</strong><br>' +
            '• Taxes (we calculate those separately)<br>' +
            '• Registration fees (we add those separately)<br><br>' +
            'This is your "out the door" price before taxes and fees!'}
          onShowGuidance={showGuidance}
        />
        
        {/* Taxes Input with $/% Toggle */}
        <div className="interactive-slider">
          <div className="label-with-guidance" style={{paddingBottom: "0.5rem"}}>
            <div style={{fontWeight: "bold"}}>Sales Taxes</div>
            <button 
            onClick={() => showGuidance(
              'Car Purchase Taxes', 
              'Car purchase taxes can vary significantly by region and vehicle type. In Canada, you may need to pay:<br><br>' +
              '<strong>Provincial Sales Tax (PST):</strong> Varies by province (5-10%)<br>' +
              '<strong>Goods and Services Tax (GST):</strong> 5% federal tax<br>' +
              '<strong>Harmonized Sales Tax (HST):</strong> Combined GST + PST in some provinces<br><br>' +
              'For accurate tax rates, check your provincial government website or consult with your dealer. ' +
              'Some provinces offer tax exemptions for electric vehicles or have different rates for used vehicles.'
            )}
            className="guidance-btn"
            title="Get guidance on car purchase taxes"
          >
            ℹ 
          </button>
          </div>
          <div className="tax-input-container">
            <div className="tax-toggle-buttons">
              <button
                type="button"
                className={`tax-toggle-btn ${!data.useTaxesPercent ? 'active' : ''}`}
                onClick={() => onInputChange('useTaxesPercent', false)}
              >
                $
              </button>
              <button
                type="button"
                className={`tax-toggle-btn ${data.useTaxesPercent ? 'active' : ''}`}
                onClick={() => onInputChange('useTaxesPercent', true)}
              >
                %
              </button>
            </div>
            {data.useTaxesPercent ? (
              <InteractiveSlider
                config={{
                  min: 0,
                  max: 20,
                  step: 0.1,
                  defaultValue: 13,
                  format: 'percent',
                  label: ''
                }}
                value={data.taxes}
                onChange={(value) => onInputChange('taxes', value)}
              />
            ) : (
              <InteractiveSlider
                config={{
                  min: 0,
                  max: 200000,
                  step: 100,
                  defaultValue: 3500,
                  format: 'currency',
                  label: ''
                }}
                value={data.taxes}
                onChange={(value) => onInputChange('taxes', value)}
              />
            )}
            
            {/* Calculated Value Display */}
            {data.useTaxesPercent && (
              <div className="calculated-value">
                <strong>Calculated Tax Amount: {formatCurrency(getTaxAmount(data))}</strong>
              </div>
            )}
          </div>

        </div>
        
        <InteractiveSlider
          config={{
            min: 0,
            max: 5000,
            step: 50,
            defaultValue: 500,
            format: 'currency',
            label: 'Fees'
          }}
          value={data.fees}
          onChange={(value) => onInputChange('fees', value)}
          guidanceTitle="Registration Fees"
          guidanceContent={'What fees do you need to pay to register the car?<br><br>' +
            '<strong>Common fees include:</strong><br>' +
            '• License plate registration ($50-200)<br>' +
            '• Title transfer fees ($20-100)<br>' +
            '• Dealer documentation fees ($200-800)<br>' +
            '• Safety inspection fees ($50-150)<br>' +
            '• Extended warranty (if purchased)<br><br>' +
            'Ask your dealer for a complete breakdown. These fees can add up quickly!'}
          onShowGuidance={showGuidance}
        />

        <InteractiveSlider
          config={{
            min: -5000,
            max: 5000,
            step: 50,
            defaultValue: 0,
            format: 'currency',
            label: 'Cash Purchase Fees'
          }}
          value={data.cashPurchaseFees}
          onChange={(value) => onInputChange('cashPurchaseFees', value)}
          guidanceTitle="Cash Purchase Fees"
          guidanceContent={'<strong>⚠️ ONLY applies to the "Buy" scenario (cash purchase) - NOT Finance or Lease</strong><br><br>' +
            'Some dealers prefer financing and may charge a surcharge for cash purchases, or offer a discount:<br><br>' +
            '<strong>Surcharge (positive value):</strong><br>' +
            '• Dealer loses financing commission<br>' +
            '• Common: $1,000-$2,000 extra for cash<br><br>' +
            '<strong>Discount (negative value):</strong><br>' +
            '• Dealer may offer cash discount<br>' +
            '• Enter as negative (e.g., -$500)<br><br>' +
            '<strong>Note:</strong> Tax is applied to this fee.'}
          onShowGuidance={showGuidance}
        />

        {/* Maintenance Input - Simple or Detailed */}
        <div className="interactive-slider">
          <div className="label-with-guidance" style={{paddingBottom: "0.5rem"}}>
            <div style={{fontWeight: "bold"}}>Maintenance Costs</div>
 <button
            type="button"
            className="guidance-btn"
            title="Guidance: Maintenance Costs"
            onClick={() => showGuidance(
              'Maintenance Costs', 
              'Maintenance costs increase over time as cars age:<br><br>' +
              '<strong>Simple Mode:</strong> One annual average for all years<br>' +
              '<strong>Detailed Mode:</strong> Year-by-year planning<br><br>' +
              '<strong>Typical costs by year:</strong><br>' +
              '• Year 1-2: $500-1,500 (basic service)<br>' +
              '• Year 3-4: $1,000-2,500 (major services)<br>' +
              '• Year 5+: $1,500-4,000+ (wear items, repairs)<br><br>' +
              'Consider warranty coverage and vehicle reliability when planning!'
            )}
          >ℹ</button></div>
          <div className="input-grid">
            <div className="maintenance-input-section">
              <div className="tax-toggle-buttons">
                <button
                  type="button"
                  className={`tax-toggle-btn ${!data.useDetailedMaintenance ? 'active' : ''}`}
                  onClick={() => onInputChange('useDetailedMaintenance', false)}
                >
                  Simple
                </button>
                <button
                  type="button"
                  className={`tax-toggle-btn ${data.useDetailedMaintenance ? 'active' : ''}`}
                  onClick={() => onInputChange('useDetailedMaintenance', true)}
                >
                  Detailed
                </button>
              </div>              
              {!data.useDetailedMaintenance ? (
                <InteractiveSlider
                  config={{
                    min: 0,
                    max: 5000,
                    step: 100,
                    defaultValue: 1500,
                    format: 'currency',
                    label: 'Annual Maintenance Cost'
                  }}
                  value={data.maintenanceCost}
                  onChange={(value) => onInputChange('maintenanceCost', value)}
                />
              ) : (
                <div className="detailed-inputs">
                  {Array.from({ length: Math.min(data.ownershipTimeframe, 5) }, (_, i) => i + 1).map(year => (
                    <div key={year} className="detailed-input">
                      <label htmlFor={`maintenance-year-${year}`}>
                        Year {year}
                      </label>
                      <input
                        id={`maintenance-year-${year}`}
                        type="number"
                        min="0"
                        step="100"
                        value={data[`maintenanceYear${year}` as keyof CarComparisonData] as number}
                        onChange={(e) => onInputChange(`maintenanceYear${year}` as keyof CarComparisonData, parseFloat(e.target.value) || 0)}
                        className="detailed-number-input"
                      />
                    </div>
                  ))}
                  {data.ownershipTimeframe > 5 && (
                    <div className="detailed-input">
                      <label htmlFor="maintenance-year-6-plus">
                        Year 6+
                      </label>
                      <input
                        id="maintenance-year-6-plus"
                        type="number"
                        min="0"
                        step="100"
                        value={data.maintenanceYear6Plus}
                        onChange={(e) => onInputChange('maintenanceYear6Plus', parseFloat(e.target.value) || 0)}
                        className="detailed-number-input"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Depreciation Header */}
        <div className="interactive-slider">
        <div className="label-with-guidance" style={{paddingBottom: "0.5rem"}}>
          <b>Depreciation</b>
          <button
            onClick={() => showGuidance(
              'Car Depreciation Information',
              'Understanding car depreciation is crucial for accurate cost calculations.<br><br><strong>Key Facts:</strong><br>• New cars typically lose 20-30% of their value in the first year<br>• Luxury cars often depreciate faster than economy cars<br>• Depreciation rates vary by brand, model, and market conditions<br><br><strong>Research Resources:</strong><br>• <a href="https://caredge.com" target="_blank" rel="noopener noreferrer">CarEdge.com</a> - Comprehensive depreciation data by make/model<br>• <a href="https://www.kbb.com" target="_blank" rel="noopener noreferrer">Kelley Blue Book</a> - Used car values and depreciation trends<br>• <a href="https://www.edmunds.com" target="_blank" rel="noopener noreferrer">Edmunds</a> - True Cost to Own data including depreciation<br><br><strong>Tips:</strong><br>• Check specific model depreciation rates before buying<br>• Consider certified pre-owned for better value retention<br>• Factor in market conditions and economic trends'
            )}
            className="guidance-btn"
            title="Get guidance on car depreciation"
          >
            ℹ
          </button>
        </div>

        {/* Depreciation Input with $/% Toggle */}
          <div className="tax-input-container">
            <div className="tax-toggle-buttons">
              <button
                type="button"
                className={`tax-toggle-btn ${!data.useDepreciationPercent ? 'active' : ''}`}
                onClick={() => onInputChange('useDepreciationPercent', false)}
              >
                $
              </button>
              <button
                type="button"
                className={`tax-toggle-btn ${data.useDepreciationPercent ? 'active' : ''}`}
                onClick={() => onInputChange('useDepreciationPercent', true)}
              >
                %
              </button>
            </div>
            {data.useDepreciationPercent ? (
              <InteractiveSlider
                config={{
                  min: 0,
                  max: 100,
                  step: 1,
                  defaultValue: 50,
                  format: 'percent',
                  label: ''
                }}
                value={data.depreciationPercent}
                onChange={(value) => onInputChange('depreciationPercent', value)}
              />
            ) : (
              <InteractiveSlider
                config={{
                  min: 0,
                  max: 1000000,
                  step: 1000,
                  defaultValue: 15000,
                  format: 'number',
                  label: ''
                }}
                value={data.expectedValueAtEnd}
                onChange={(value) => onInputChange('expectedValueAtEnd', value)}
              />
            )}
            {/* Calculated Value Display */}
            {data.useDepreciationPercent && (
              <div className="calculated-value">
                <strong>Calculated Resale Value: {formatCurrency(getExpectedValueAtEnd(data))}</strong>
              </div>
            )}
          </div>
        
        </div>        
        <div className="interactive-slider">
        <div className="label-with-guidance" style={{paddingBottom: "0.5rem"}}>
          <b>Selling Costs</b>
          <button
            onClick={() => showGuidance(
              'Car Selling Costs',
              'When you sell your car, you may incur various costs that reduce your net proceeds:<br><br><strong>Common Selling Costs:</strong><br>• Advertising fees (online listings, classified ads)<br>• Inspection fees (safety inspection, emissions test)<br>• Documentation fees (title transfer, registration)<br>• Commission fees (if using a broker or dealer)<br>• Cleaning and detailing costs<br>• Minor repairs to make the car more saleable<br><br><strong>Percentage vs. Fixed Amount:</strong><br>• <strong>Percentage:</strong> Good for estimating based on selling price<br>• <strong>Fixed Amount:</strong> Good for known specific costs<br><br><strong>Tips:</strong><br>• Research local market rates for similar services<br>• Factor in your time and effort if selling privately<br>• Consider professional appraisal for high-value vehicles'
            )}
            className="guidance-btn"
            title="Get guidance on car selling costs"
          >
            ℹ
          </button>
        </div>

        {/* Selling Costs Input with $/% Toggle */}

          <div className="tax-input-container">
            <div className="tax-toggle-buttons">
              <button
                type="button"
                className={`tax-toggle-btn ${!data.useSellingCostsPercent ? 'active' : ''}`}
                onClick={() => onInputChange('useSellingCostsPercent', false)}
              >
                $
              </button>
              <button
                type="button"
                className={`tax-toggle-btn ${data.useSellingCostsPercent ? 'active' : ''}`}
                onClick={() => onInputChange('useSellingCostsPercent', true)}
              >
                %
              </button>
            </div>
            {data.useSellingCostsPercent ? (
              <InteractiveSlider
                config={{
                  min: 0,
                  max: 100,
                  step: 1,
                  defaultValue: 5,
                  format: 'percent',
                  label: ''
                }}
                value={data.sellingCostsPercent}
                onChange={(value) => onInputChange('sellingCostsPercent', value)}
              />
            ) : (
              <InteractiveSlider
                config={{
                  min: 0,
                  max: 200000,
                  step: 1000,
                  defaultValue: 500,
                  format: 'number',
                  label: ''
                }}
                value={data.sellingCosts}
                onChange={(value) => onInputChange('sellingCosts', value)}
              />
            )}
            {/* Calculated Selling Costs Display */}
            {data.useSellingCostsPercent && (
              <div className="calculated-value">
                <strong>Calculated Selling Costs: {formatCurrency(getSellingCosts(data))}</strong>
              </div>
            )}
          </div>
        </div>
     </>
    ))
  );
};

export default BuyInputs;